import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useInView } from 'react-intersection-observer'
import { useExperimentContext } from '@/experimentation'
import { useToggle } from '@/hooks/useToggle'
import { getBasicPageMetaData, Seo } from '@/layout/Seo'
import { ShopifyProductWithBundles } from '@/services/Shopify/ShopifyService'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { BonusGift } from '@/views/PreSales/PreSalesGiveawayView/components/BonusGift'
import { BonusGiftCard } from '@/views/PreSales/PreSalesGiveawayView/components/BonusGiftCard'
import { GiveawayFooter } from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayFooter'
import GiveawayHero from '@/views/PreSales/PreSalesGiveawayView/components/GiveawayHero'
import { PresaleChipSelectorCard } from '@/views/PreSales/PreSalesGiveawayView/components/PresaleChipSelectorCard'
import { ShareButton } from '@/views/PreSales/PreSalesGiveawayView/components/ShareButton'
import { ShareModal } from '@/views/PreSales/PreSalesGiveawayView/components/ShareModal'
import { TabHeader } from '@/views/PreSales/PreSalesPackagesView/components/TabHeader'
import { useBonusGift } from '@/views/PreSales/hooks/useBonusGift'
import { GiveawayRefundCard } from './components/GiveawayRefundCard'
import { GiveawaySectionHeader } from './components/GiveawaySectionHeader'
import { PresaleDetailsTab } from './tabs/PresaleDetailsTab'
import { PresalesFAQsTab } from './tabs/PresaleFAQsTab'
import { PresaleUpdatesTab } from './tabs/PresaleUpdatesTab'
import { PrizeItemsTab } from './tabs/PrizeItemsTab'

interface Props {
  theatricalSlug: string
  tickets: ShopifyProductWithBundles[]
}

type BonusPrizeVariation = 'off' | 'modal' | 'card-and-modal'

export type AvailableTabsPresales = 'details' | 'updates' | 'faq' | 'prizeItems' | 'rules'

export const PreSalesGiveawayView: FC<Props> = ({ tickets, theatricalSlug }) => {
  const [activeTab, setActiveTab] = useState<AvailableTabsPresales>('details')
  const { locale } = useLocale()
  const { push, query } = useRouter()
  const topElement = useRef<null | HTMLDivElement>(null)
  const packagesDesktopRef = useRef<null | HTMLDivElement>(null)
  const packagesMobileRef = useRef<null | HTMLDivElement>(null)
  const packagesMobileRefNew = useRef<null | HTMLDivElement>(null)
  const [displayStickyBtn, setDisplayStickyBtn] = useState(true)
  const [multiplier, setMultiplier] = useState<number | null>(null)
  const giveawayElement = useRef<HTMLDivElement>(null)
  const { t } = useTranslate('tickets')
  const { ref: productRef, inView: isProductInView } = useInView({ threshold: 0 })
  const { ref: heroRef, inView: isHeroInView } = useInView({ threshold: 0.9 })
  const { ref: tabsRef, inView: isTabsInView } = useInView({ threshold: 0 })
  const [isShareModalOpen, setIsShareModalOpen] = useToggle(false)
  const { bonusGift, hasSeenBonusGift, setHasSeenBonusGift } = useBonusGift()
  const [isBonusPrizeModalOpen, setIsBonusPrizeModalOpen] = useToggle(false)
  const [bonusPrizeExperiment, setBonusPrizeExperiment] = useState<BonusPrizeVariation>('off')
  const { getFeatureValue, ready } = useExperimentContext()

  useEffect(() => {
    if (!ready) return
    const prizeExperiment = getFeatureValue('bonus-prize-v2', 'off')
    setBonusPrizeExperiment((prizeExperiment as BonusPrizeVariation) ?? 'off')
  }, [getFeatureValue, ready])

  useEffect(() => {
    if (bonusPrizeExperiment === 'modal' && !hasSeenBonusGift) {
      setIsBonusPrizeModalOpen(true)
    }
  }, [bonusPrizeExperiment, hasSeenBonusGift, setIsBonusPrizeModalOpen])

  useEffect(() => {
    if (multiplier === null) {
      fetch('https://theatrical.angelstudios.com/api/v1/open/weeks-until/homestead')
        .then((res) => res.json())
        .then((data) => (data.weeks_until_release ? setMultiplier(data.weeks_until_release) : null))
    }
  }, [multiplier])

  useEffect(() => {
    if (isProductInView || isHeroInView) {
      setDisplayStickyBtn(false)
    } else {
      setDisplayStickyBtn(true)
    }
  }, [isHeroInView, isProductInView])

  const scrollToProducts = useCallback(() => {
    packagesDesktopRef?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
    packagesMobileRef?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
    packagesMobileRefNew?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
  }, [])

  const scrollToPrizes = useCallback(() => {
    topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
    setActiveTab('prizeItems')
  }, [])

  const scrollToContent = useCallback(() => {
    giveawayElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
  }, [])

  const scrollToTab = useCallback((tab: AvailableTabsPresales) => {
    setActiveTab(tab)
    topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
  }, [])

  const handleRefundInfoClick = useCallback(() => {
    scrollToTab('faq')
    push('#refundPolicy')
  }, [push, scrollToTab])

  return (
    <div>
      <Seo
        {...getBasicPageMetaData({
          cloudinaryImagePath: 'v1730485364/Giveaway_Banner_Edit.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          description: t(
            'homesteadTicketsSeoTitle',
            'Buy {{projectName}} tickets for a Chance to Win a $300K Giveaway!',
            {
              projectName: 'Homestead',
            },
          ),
          locale,
          path: `/pre-sales/homestead/giveaway`,
          title: t('ticketsSEODescriptionPreSalesV2', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
            projectName: 'Homestead',
          }),
        })}
      />
      <GiveawayHero
        heroRef={heroRef}
        trailerUrl="https://www.youtube.com/embed/V5zQ6XyHhrs"
        backgroundVideoSrc="https://content.angelstudios.com/homestead/Homestead-Hero-Loop.mp4"
        title={t('enterHomesteadGiveawayV3', 'Watch Homestead in Theaters and You could Win a $300k Homestead!')}
        onCtaClick={scrollToProducts}
        onLearnMoreClick={scrollToContent}
        multiplier={multiplier}
      />

      {tickets && (
        <div ref={packagesMobileRefNew} className="w-full overflow-y-auto bg-white md:bg-[#F5F5F5] lg:hidden">
          <div className="top-0 flex w-full flex-col gap-6 bg-white px-4 py-16 md:sticky md:bg-[#F5F5F5] md:px-12">
            <h2 className="homestead-title-lg uppercase text-homestead-primary-blue">
              {t('selectATicketPack', 'Select a Ticket Pack')}
            </h2>

            <div className="space-y-2">
              {!!bonusGift && bonusPrizeExperiment === 'card-and-modal' && (
                <BonusGiftCard
                  bonusGift={bonusGift}
                  onLearnMoreClick={() => {
                    setIsBonusPrizeModalOpen(true)
                  }}
                />
              )}
              <GiveawayRefundCard
                title={t('giveawayRefundGuarantee', 'Showtime Guarantee')}
                description={t(
                  'giveawayRefundGuaranteeDescription',
                  'Homestead releases nationwide; if no showtimes appear near you, you can get a full refund. ',
                )}
                onInfoClick={handleRefundInfoClick}
              />
            </div>
            <PresaleChipSelectorCard
              theatricalSlug={theatricalSlug}
              productRef={productRef}
              tickets={tickets}
              multiplier={multiplier || 1}
            />
          </div>
        </div>
      )}
      <div ref={giveawayElement} className="px-4 md:px-12">
        <GiveawaySectionHeader scrollToProducts={scrollToProducts} />
      </div>
      <div ref={topElement} className="mx-auto flex grow-1 bg-[#F5F5F5]">
        <div ref={tabsRef} className="min-h-full w-full max-w-[960px] md:ml-auto lg:w-3/5">
          <div className="relative min-h-screen">
            <header className="sticky top-0 z-30 border-b-2 border-[#D3D3D6] bg-[#F5F5F5] pb-4 pt-6 text-white">
              <nav className="container mx-auto flex justify-center">
                <ul className="flex min-h-7 space-x-8 overflow-y-scroll px-4 text-black scrollbar-none">
                  <li>
                    <TabHeader
                      onClick={() => {
                        setActiveTab('details')
                        topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
                      }}
                      isActive={activeTab === 'details'}
                      text={t('details', 'Details')}
                    />
                  </li>
                  <li className="text-nowrap">
                    <TabHeader
                      onClick={() => {
                        setActiveTab('prizeItems')
                        topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
                      }}
                      isActive={activeTab === 'prizeItems'}
                      text={t('prizeItems', 'Prize Items')}
                    />
                  </li>
                  <li>
                    <TabHeader
                      onClick={() => {
                        setActiveTab('updates')
                        topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
                      }}
                      isActive={activeTab === 'updates'}
                      text={t('updates', 'Updates')}
                    />
                  </li>
                  <li>
                    <TabHeader
                      onClick={() => {
                        setActiveTab('faq')
                        topElement?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' })
                      }}
                      isActive={activeTab === 'faq'}
                      text={t('faq', 'FAQ')}
                    />
                  </li>
                </ul>
              </nav>
            </header>

            {activeTab === 'faq' && <PresalesFAQsTab />}

            {activeTab === 'updates' && <PresaleUpdatesTab scroll={scrollToProducts} />}

            {activeTab === 'details' && (
              <PresaleDetailsTab
                displayStickyBtn={displayStickyBtn}
                scrollToProducts={scrollToProducts}
                scrollToPrizes={scrollToPrizes}
              />
            )}

            {activeTab === 'prizeItems' && <PrizeItemsTab />}
          </div>
        </div>
        {tickets && (
          <div ref={packagesDesktopRef} className="hidden max-w-[480px] lg:mr-auto lg:block">
            <div className="flex w-full flex-col gap-6 bg-[#F5F5F5] px-4 py-16 md:top-0 md:px-12 shortDesktop:!static shortDesktop:top-auto">
              <h2 className="homestead-title-lg uppercase text-homestead-primary-blue">
                {t('selectATicketPack', 'Select a Ticket Pack')}
              </h2>
              <div className="space-y-2">
                {!!bonusGift && bonusPrizeExperiment === 'card-and-modal' && (
                  <BonusGiftCard
                    bonusGift={bonusGift}
                    onLearnMoreClick={() => {
                      setIsBonusPrizeModalOpen(true)
                    }}
                  />
                )}
                <GiveawayRefundCard
                  title={t('giveawayRefundGuarantee', 'Showtime Guarantee')}
                  description={t(
                    'giveawayRefundGuaranteeDescription',
                    'Homestead releases nationwide; if no showtimes appear near you, you can get a full refund. ',
                  )}
                  onInfoClick={handleRefundInfoClick}
                />
              </div>
              <PresaleChipSelectorCard
                theatricalSlug={theatricalSlug}
                productRef={productRef}
                tickets={tickets}
                multiplier={multiplier ?? 1}
              />
            </div>
          </div>
        )}
        {query?.shareEnabled === 'true' && (
          <ShareButton
            isHeroInView={isHeroInView}
            setIsShareModalOpen={setIsShareModalOpen}
            classes={isTabsInView ? 'bottom-24' : 'bottom-6'}
          />
        )}
      </div>

      <GiveawayFooter scrollToTab={scrollToTab} scrollToProducts={scrollToProducts} />

      {bonusGift && (
        <BonusGift
          isOpen={isBonusPrizeModalOpen}
          onClose={() => {
            setIsBonusPrizeModalOpen(false)
            setHasSeenBonusGift()
          }}
          bonusGift={bonusGift}
          onCtaClick={() => {
            setIsBonusPrizeModalOpen(false)
            setHasSeenBonusGift()
            scrollToProducts()
          }}
        />
      )}

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        theatricalSlug={theatricalSlug}
      />
    </div>
  )
}
